// Persistent in-editor ad placements — signed-out visitors and free accounts
// (since 2026-10-21; paid tiers never load ad code).
// Both placements are normal flow elements (never floating over content — a
// placement-policy requirement) and stay display:none until a unit actually
// fills, so nobody ever sees an empty grey box:
//   - rail:   bottom of the right properties panel
//   - banner: full-width strip between the editor row and the status bar
import { state } from './../state'
import { onAuthChange, onTierChecked, tierSettled } from './../auth/supabase'
import { effectiveTier } from './../auth/org'
import { adsConfig, adsConfigured } from './config'
import { adFreeUntil } from './gate'
import { provider } from './providers'
import { hasStaticMediavineTag } from './providers/mediavine'
import { setStoredPaid } from './paidFlag'

// Sizes are pinned (see renderUnit): the host reserves exactly the creative's
// box, so a rendered ad can never be clipped (placement policy).
//   rail:   fixed 200x200 — the largest standard size that fits the 224px panel
//   layers: fixed 200x200 — same, pinned to the 208px layers panel's bottom
//   strip:  widest standard horizontal banner that fits the strip, centered —
//           a "full width" responsive slot gets a left-aligned 970x90 from
//           Google, which reads as half-loaded on wide screens. Never two
//           units side by side (placement policy).
//   mobile: fixed-height 50px banner docked at the bottom of the mobile flow
//           (#mobileEditor is a flex column; appended last = bottom, in flow).
const stripSize = (w: number): { width: number; height: number } =>
  w >= 980 ? { width: 970, height: 90 }
  : w >= 736 ? { width: 728, height: 90 }
  : w >= 476 ? { width: 468, height: 60 }
  : { width: 320, height: 50 }

const PANEL_CSS = 'visibility:hidden;margin-top:auto;padding:8px 0;text-align:center'

const HOSTS: { key: 'slotRail' | 'slotBanner' | 'slotLayers' | 'slotMobile'; id: string; css: string; size: (hostWidth: number) => { width: number; height: number }; mount: () => HTMLElement | null; before?: () => HTMLElement | null }[] = [
  {
    key: 'slotRail',
    id: 'adRail',
    css: PANEL_CSS,
    size: () => ({ width: 200, height: 200 }),
    mount: () => document.getElementById('propertiesPanel'),
  },
  {
    key: 'slotLayers',
    id: 'adLayers',
    css: PANEL_CSS,
    size: () => ({ width: 200, height: 200 }),
    mount: () => document.getElementById('layersPanel'),
  },
  {
    key: 'slotBanner',
    id: 'adStrip',
    css: 'visibility:hidden;width:100%;height:90px;background:#0f172a;text-align:center',
    size: stripSize,
    mount: () => document.getElementById('statusBar')?.parentElement ?? null,
    before: () => document.getElementById('statusBar'),
  },
  {
    key: 'slotMobile',
    id: 'adMobile',
    css: 'visibility:hidden;width:100%;height:50px;background:#0f172a;text-align:center;flex-shrink:0',
    size: () => ({ width: 0, height: 50 }),
    mount: () => document.getElementById('mobileEditor'),
  },
]

function eligible(): boolean {
  const cfg = adsConfig()
  return cfg.enabled && cfg.audience !== 'off' && adsConfigured(cfg)
    && adFreeUntil() <= Date.now() && (!state.currentUser || effectiveTier() === 'free')
}

/** After each tier check: remember the plan for the head marker, and if this
 *  page view let ads through for a paid account (stale plan, e.g. right after
 *  upgrading), reload once so Mediavine's disable-all switch applies. */
const RELOAD_KEY = 'olm_ad_state_reload'
function syncAdState(): void {
  if (state.currentUser) {
    const paid = effectiveTier() !== 'free'
    setStoredPaid(paid)
    if (paid && hasStaticMediavineTag() && document.documentElement.classList.contains('olm-ads')) {
      let last = 0
      try { last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0 } catch { /* storage blocked */ }
      if (Date.now() - last > 60_000) {
        try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())) } catch { /* storage blocked */ }
        window.location.reload()
        return
      }
    }
  }
  refreshAdPlacements()
}

function removeOwnHosts(): void {
  for (const h of HOSTS) document.getElementById(h.id)?.remove()
}

function removeAll(): void {
  removeOwnHosts()
  ensureMediavineLeft(false)
}

/** Mediavine targets its 300×250 unit at this container (bottom of the layers
 *  panel, which widens to fit — editor.css). Paid accounts never get it. */
export const MEDIAVINE_LEFT_ID = 'olmAdLeft'
function ensureMediavineLeft(on: boolean): void {
  const cur = document.getElementById(MEDIAVINE_LEFT_ID)
  if (!on) { cur?.remove(); return }
  const panel = document.getElementById('layersPanel')
  if (cur || !panel) return
  const host = document.createElement('div')
  host.id = MEDIAVINE_LEFT_ID
  host.dataset.testid = MEDIAVINE_LEFT_ID
  // Mediavine slot target (their rep, 2026-10-09): restricts the slot to
  // 300×250 so the wrapper doesn't reserve room for larger creatives, which
  // pushed the canvas column below the layers panel.
  host.className = 'mv_slot_target_desktop'
  host.setAttribute('data-slot', 'content')
  host.setAttribute('data-hint-slot-sizes', '300x250')
  panel.appendChild(host)
}

/** (Re-)evaluate the persistent placements against the current user/config. */
export function refreshAdPlacements(): void {
  if (!eligible()) { removeAll(); return }
  const cfg = adsConfig()
  if (cfg.provider === 'mediavine') {
    // Mediavine inserts its own units (dashboard-configured). Its wrapper can't
    // be unloaded, so wait until the session check has settled and load it
    // only if the visitor is (still) ad-eligible. Our own hosts are not used.
    // Keep an existing #olmAdLeft: Mediavine puts its ad in a sibling
    // .mv-ad-box, so recreating the target (e.g. on sign-up) gets a 2nd unit.
    removeOwnHosts()
    // Only once the session check has settled (no flash of the wide panel for
    // paid accounts), and — on builds with the static wrapper — only when the
    // head marker flagged this page view as ad-eligible (class "olm-ads"); a
    // page view marked ad-free has Mediavine's disable-all element and would
    // leave the container empty.
    void tierSettled.then(() => {
      if (!eligible()) { ensureMediavineLeft(false); return }
      void provider().loadSdk()
      const staticTag = !!document.querySelector('script[src*="scripts.mediavine.com/tags/"]')
      ensureMediavineLeft(!staticTag || document.documentElement.classList.contains('olm-ads') || !document.getElementById('mediavine-settings'))
    })
    return
  }
  for (const h of HOSTS) {
    const slot = cfg[h.key]
    if (!slot || document.getElementById(h.id)) continue
    const parent = h.mount()
    if (!parent) continue
    const host = document.createElement('div')
    host.id = h.id
    host.dataset.testid = h.id
    // visibility:hidden, NOT display:none — AdSense measures available width at
    // push time, and a display:none host measures 0 wide, which kills the fill.
    host.style.cssText = h.css
    const anchor = h.before?.()
    if (anchor) parent.insertBefore(host, anchor)
    else parent.appendChild(host)
    void provider().renderUnit(host, slot, h.size(host.clientWidth)).then((r) => {
      // Unfilled/blocked hosts are removed, not left blank; the modal owns the
      // adblock-wall policy — a passive placement never nags.
      if (r === 'filled' && eligible()) host.style.visibility = 'visible'
      else host.remove()
    }).catch(() => host.remove())
  }
}

/** Boot hook: mount for eligible users now and on every auth change. */
export function initAdPlacements(): void {
  refreshAdPlacements()
  onAuthChange(refreshAdPlacements)
  onTierChecked(syncAdState)
}
